// Grid geometry, pure: placement, overlap, snapping. The component turns
// pointer movement into cells and asks these what is allowed; the server
// clamps again on save, so nothing here is a security boundary.
export interface Rect { x: number; y: number; w: number; h: number }
export interface Placed extends Rect { id: string }
export interface MinSize { w: number; h: number }

export const COLUMNS = 12;
/** Rows are bounded so a stray drag cannot push the page kilometres down. */
export const MAX_ROWS = 500;

export function overlaps(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** Keeps a rectangle on the grid and at least its minimum size. */
export function clampRect(r: Rect, min: MinSize, columns = COLUMNS): Rect {
  const w = Math.max(min.w, Math.min(columns, Math.round(r.w)));
  const h = Math.max(min.h, Math.min(MAX_ROWS, Math.round(r.h)));
  const x = Math.max(0, Math.min(columns - w, Math.round(r.x)));
  const y = Math.max(0, Math.min(MAX_ROWS, Math.round(r.y)));
  return { x, y, w, h };
}

/** True when `candidate` overlaps any other item. Overlap is refused rather
 * than resolved by pushing neighbours around: what you dropped is where it
 * lands, or it does not land. */
export function collides(items: Placed[], candidate: Placed): boolean {
  return items.some((it) => it.id !== candidate.id && overlaps(it, candidate));
}

/** The first free slot for a w×h widget, scanning rows top to bottom and
 * columns left to right; below everything when nothing fits between. */
export function firstFree(items: Placed[], w: number, h: number, columns = COLUMNS): { x: number; y: number } {
  const bottom = rows(items);
  for (let y = 0; y <= bottom; y++) {
    for (let x = 0; x + w <= columns; x++) {
      if (!collides(items, { id: "", x, y, w, h })) return { x, y };
    }
  }
  return { x: 0, y: bottom };
}

/** Rows the layout occupies. */
export function rows(items: Placed[]): number {
  return items.reduce((m, it) => Math.max(m, it.y + it.h), 0);
}

/** Moves one item; null when the target overlaps another. */
export function move(items: Placed[], id: string, x: number, y: number, min: MinSize, columns = COLUMNS): Placed[] | null {
  const it = items.find((i) => i.id === id);
  if (!it) return null;
  const next = { ...it, ...clampRect({ ...it, x, y }, min, columns) };
  if (collides(items, next)) return null;
  return items.map((i) => (i.id === id ? next : i));
}

/** Resizes one item from its top-left corner; null when the result overlaps. */
export function resize(items: Placed[], id: string, w: number, h: number, min: MinSize, columns = COLUMNS): Placed[] | null {
  const it = items.find((i) => i.id === id);
  if (!it) return null;
  const next = { ...it, ...clampRect({ ...it, w, h }, min, columns) };
  // Growing past the right edge shrinks back rather than sliding left: the
  // corner the person is dragging is the one that should move.
  if (next.x !== it.x) {
    next.w = Math.max(min.w, columns - it.x);
    next.x = it.x;
  }
  if (collides(items, next)) return null;
  return items.map((i) => (i.id === id ? next : i));
}

/** Pointer displacement in pixels to whole cells. */
export function cellsFromPixels(dx: number, dy: number, colWidth: number, rowHeight: number): { dc: number; dr: number } {
  return { dc: colWidth > 0 ? Math.round(dx / colWidth) : 0, dr: rowHeight > 0 ? Math.round(dy / rowHeight) : 0 };
}
